import { useAtomValue } from 'jotai'
import { useRef } from 'react'
import {
	WidgetInputColorField,
	WidgetLabel,
	WidgetOptInCheckbox,
	WidgetSelectDropdown,
	type WidgetSelectDropdownOption,
} from '../../../../rapid-ui'
import { externalCanvasDataAtom } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { TextColor, TextColorMode } from '../../data/v1'
import { COLOR_REF_FULL_LABELS, COLOR_REF_MODES, ColorDot, type ColorRefMode } from './color-swatches'

export type ColorModeInspectorProps = {
	label: string
	value: TextColor
	onChange: (value: TextColor) => void
	/**
	 * Omit for a colour that is always explicit. Present, it renders the checkbox that
	 * decides whether this layer supplies the colour at all — inheriting from an ancestor
	 * for text, or leaving the drawn shape's own paint alone for a shape fill.
	 */
	optIn?: {
		optedIn: boolean
		onOptIn: () => void
		onOptOut: () => void
	}
}

/**
 * Picks between the branding palette references and a literal colour, as well as supporting no
 * color at all.
 */
export function ColorModeInspector({ label, value, onChange, optIn }: ColorModeInspectorProps) {
	const store = useCanvasStore()
	const externalData = useAtomValue(externalCanvasDataAtom, { store })
	const prevModeRef = useRef(value.mode)
	const colorJustSelected = prevModeRef.current !== 'color' && value.mode === 'color'
	prevModeRef.current = value.mode

	const externalColors: Record<ColorRefMode, string> = {
		foregroundColor: externalData.foregroundColor,
		backgroundColor: externalData.backgroundColor,
		accentForegroundColor: externalData.accentForegroundColor,
		accentBackgroundColor: externalData.accentBackgroundColor,
	}

	const options: WidgetSelectDropdownOption<TextColorMode>[] = [
		...COLOR_REF_MODES.map((ref) => ({
			id: ref as TextColorMode,
			label: (
				<div className="flex items-center gap-2">
					<ColorDot color={externalColors[ref] || '#ccc'} />
					{COLOR_REF_FULL_LABELS[ref]}
				</div>
			),
		})),
		{
			id: 'color',
			label: (
				<div className="flex items-center gap-2">
					<ColorDot color={value.color} />
					Custom color
				</div>
			),
		},
	]

	const dropdown = (
		<WidgetSelectDropdown
			options={options}
			value={value.mode}
			onChange={(mode) => onChange({ ...value, mode })}
			label={label}
		/>
	)

	return (
		<div className="flex flex-col gap-2">
			<WidgetLabel label={label}>
				{optIn
					? (
						<WidgetOptInCheckbox optedIn={optIn.optedIn} onOptIn={optIn.onOptIn} onOptOut={optIn.onOptOut}>
							{dropdown}
						</WidgetOptInCheckbox>
					)
					: dropdown}
			</WidgetLabel>

			{value.mode === 'color' && (
				<WidgetInputColorField
					value={value.color}
					onChange={(color) => onChange({ ...value, color })}
					label="Color"
					initialOpen={colorJustSelected}
				/>
			)}
		</div>
	)
}
